import React, { useState } from "react";
import { Worker, Viewer, SpecialZoomLevel } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { printPlugin } from "@react-pdf-viewer/print";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "@react-pdf-viewer/zoom/lib/styles/index.css";
import CircularProgress from "@mui/material/CircularProgress";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import pdfWorker from "pdfjs-dist/build/pdf.worker.entry";
import Download from "../../../assets/img/download.svg";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

interface PdfViewerProps {
  fileUrl: string;
  customFilename?: string; // Optional custom filename prop
}

const PdfViewer: React.FC<PdfViewerProps> = ({
  fileUrl,
  customFilename = "document.pdf", // Default filename if not provided
}) => {
  const [loading, setLoading] = useState(true);
  const [isDownloadHover, setIsDownloadHover] = useState(false);
  const printPluginInstance = printPlugin();
  const zoomPluginInstance = zoomPlugin();

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    renderToolbar: (Toolbar) => (
      <Toolbar>
        {(slots) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div style={{ flex: 1 }} />
            <div style={{ display: "flex", gap: "10px" }}>
              {slots.ZoomOut && <slots.ZoomOut />}
              {slots.Zoom && <slots.Zoom />}
              {slots.ZoomIn && <slots.ZoomIn />}
            </div>
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginRight: "20px",
              }}
            >
              <div style={{ paddingTop: "6px" }}>{slots.Print && <slots.Print />}</div> {/* Custom Download Button */}
              <button
                onClick={handleCustomDownload}
                onMouseEnter={() => setIsDownloadHover(true)}
                onMouseLeave={() => setIsDownloadHover(false)}
                style={{
                  background: isDownloadHover ? "black" : "transparent", // matches hover color
                  border: "none",
                  cursor: "pointer",
                  padding: "8px",
                  borderRadius: "16x",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title="Download"
              >
                <img
                  src={Download}
                  alt="Download"
                  width={24}
                  height={24}
                  style={{ display: "block", filter: "invert(1)" }}
                />
              </button>
            </div>
          </div>
        )}
      </Toolbar>
    ),
    sidebarTabs: () => [],
  });

  const handleCustomDownload = async () => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();

      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary anchor element for download
      const a = document.createElement("a");
      a.href = url;
      a.download = customFilename; // Use custom filename
      document.body.appendChild(a);
      a.click();

      // Clean up
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  return (
    <div
      style={{
        height: "100vh",
        width: "100%",
        position: "relative",
        background: "#1e1e1e",
      }}
    >
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.25)",
            zIndex: 999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress color="inherit" size={60} />
        </div>
      )}
      <Worker workerUrl={pdfWorker}>
        <Viewer
          fileUrl={fileUrl}
          theme="dark"
          defaultScale={SpecialZoomLevel.ActualSize}
          plugins={[defaultLayoutPluginInstance, printPluginInstance, zoomPluginInstance]}
          onDocumentLoad={() => setTimeout(() => setLoading(false), 400)}
        />
      </Worker>
      {/* Icon fill override */}
      <style>
        {`
         .rpv-core__icon {
         fill: white !important;   
         stroke: black !important;    
         }
         .rpv-core__inner-page{
         margin-top:30px !important
         }
        `}
      </style>
    </div>
  );
};

export default PdfViewer;
